import {
    IQueryFilterCriteria,
    IQueryFilterCriteriaSelectionValue,
} from '../../../../interfaces/IQuery';

import Common from '../../../../stores/Common';
import { ConditionSelectorEnums } from '../../../../utilities/Constants';
import { ICaseStatusLookupModel } from '../../../../interfaces/ICase';
import { hasBlankCondition } from '../../../../utilities/UIHelper';

const conditionMeansTrue = (condition: string | boolean | undefined) =>
    condition === 'true' || condition === true;

export function SelectedValuesSummary({ item }: { item: IQueryFilterCriteria }) {
    let fieldSummary = '';

    const isBlankishCondition = hasBlankCondition(item);

    if (isBlankishCondition) {
        fieldSummary += conditionMeansTrue(item.condition) ? 'Blank' : 'Not Blank';
    } else {
        const fieldSummaries = item?.selectedValues?.map((selectedValue) => selectedValue.label);
        if (item.condition === ConditionSelectorEnums.NotEqualTo) {
            fieldSummary += 'Not Equal To '
        }
        
        if (fieldSummaries?.length === 0) {
            fieldSummary += 'Not Set';
        } else {
            fieldSummary += fieldSummaries?.join(' | ');
        }
    }

    return <>{fieldSummary}</>;
}

export function DateRangeSummary({ item }: { item: IQueryFilterCriteria }) {
    let fieldSummary = '';

    if (item.condition === ConditionSelectorEnums.Range) {
        if ((item.dateRangeInterval ?? 0) < 0) {
            fieldSummary = `Past ${Math.abs(item.dateRangeInterval ?? 0)} days`;
        } else {
            fieldSummary = `Next ${item.dateRangeInterval} days`;
        }
        return <>{fieldSummary}</>;
    }

    const isBlankishCondition = hasBlankCondition(item);

    if (isBlankishCondition) {
        fieldSummary += conditionMeansTrue(item.condition) ? 'Blank' : 'Not Blank';
    } else {
        if (!item?.startDate && !item?.endDate) {
            fieldSummary += 'Not Set';
        } else if (item?.startDate && !item?.endDate)
            fieldSummary += `After ${Common.dateFormat(item.startDate)}`;
        else if (!item?.startDate && item?.endDate)
            fieldSummary += `Before ${Common.dateFormat(item.endDate)}`;
        else if (item?.startDate && item?.endDate)
            fieldSummary += `Between ${Common.dateFormat(item.startDate)} and ${Common.dateFormat(
                item.endDate
            )}`;
    }

    return <>{fieldSummary}</>;
}

export function BooleanSummary({ item }: { item: IQueryFilterCriteria }) {
    let fieldSummary = '';

    const isBlankishCondition = hasBlankCondition(item);

    if (isBlankishCondition) {
        fieldSummary += conditionMeansTrue(item.condition) ? 'Blank' : 'Not Blank';
    } else {
        if (item.condition === ConditionSelectorEnums.NotEqualTo) {
            fieldSummary += 'Not Equal To '
        }
        fieldSummary += item.booleanValue ? 'Yes' : 'No';
    }
    return <>{fieldSummary}</>;
}

export function TextSummary({ item }: { item: IQueryFilterCriteria }) {
    const text = item.text ?? 'Not Set';
    let fieldSummary = '';

    const isBlankishCondition = hasBlankCondition(item);

    if (isBlankishCondition) {
        fieldSummary += conditionMeansTrue(item.condition) ? 'Blank' : 'Not Blank';
    } else {
        if (item.condition === ConditionSelectorEnums.NotEqualTo) {
            fieldSummary += 'Not Equal To '
        }
        fieldSummary += text;
    }
    return <>{fieldSummary}</>;
}

export function CaseStatusSummary({
    item,
    findStatus,
}: {
    item: IQueryFilterCriteria;
    findStatus: (value: IQueryFilterCriteriaSelectionValue) => ICaseStatusLookupModel | undefined;
}) {
    let fieldSummary = '';

    const isBlankishCondition = hasBlankCondition(item);

    if (isBlankishCondition) {
        fieldSummary += conditionMeansTrue(item.condition) ? 'Blank' : 'Not Blank';
    } else {
        const fieldSummaries = item?.selectedValues?.map((selectedValue) => {
            const status = findStatus(selectedValue);
            return status?.name;
        });
        if ((fieldSummaries?.length ?? 0) === 0) {
            fieldSummary += 'Not Set';
        } else {
            fieldSummary += fieldSummaries?.filter((summary) => summary).join(' | ');
        }
    }

    return <>{fieldSummary}</>;
}

export function CurrencySummary({ item }: { item: IQueryFilterCriteria }) {
    let fieldSummary = '';

    const isBlankishCondition = hasBlankCondition(item);

    if (isBlankishCondition) {
        fieldSummary += conditionMeansTrue(item.condition) ? 'Blank' : 'Not Blank';
    } else {
        if (!item?.numericValueFrom && !item?.numericValueTo) {
            fieldSummary += 'Not Set';
        } else if (!!item.numericValueFrom && !!item.numericValueTo) {
            fieldSummary += `Between ${Common.formatCurrency(
                item.numericValueFrom?.toString()
            )} and ${Common.formatCurrency(item.numericValueTo?.toString())}`;
        } else if (item.numericValueFrom) {
            fieldSummary += `Greater than ${Common.formatCurrency(
                item.numericValueFrom?.toString()
            )}`;
        } else {
            fieldSummary += `Less than ${Common.formatCurrency(item.numericValueTo?.toString())}`;
        }
    }

    return <>{fieldSummary}</>;
}

export function CurrencyWithCentsSummary({ item }: { item: IQueryFilterCriteria }) {
    let fieldSummary = '';

    const isBlankishCondition = hasBlankCondition(item);

    if (isBlankishCondition) {
        fieldSummary += conditionMeansTrue(item.condition) ? 'Blank' : 'Not Blank';
    } else {
        if (!item?.numericValueFrom && !item?.numericValueTo) {
            fieldSummary += 'Not Set';
        } else if (!!item.numericValueFrom && !!item.numericValueTo) {
            fieldSummary += `Between ${Common.formatCurrencyWithCents(
                item.numericValueFrom?.toString()
            )} and ${Common.formatCurrencyWithCents(item.numericValueTo?.toString())}`;
        } else if (item.numericValueFrom) {
            fieldSummary += `Greater than ${Common.formatCurrencyWithCents(
                item.numericValueFrom?.toString()
            )}`;
        } else {
            fieldSummary += `Less than ${Common.formatCurrencyWithCents(item.numericValueTo?.toString())}`;
        }
    }

    return <>{fieldSummary}</>;
}

export function NumericSummary({ item }: { item: IQueryFilterCriteria }) {
    let fieldSummary = '';

    const isBlankishCondition = hasBlankCondition(item);

    if (isBlankishCondition) {
        fieldSummary += conditionMeansTrue(item.condition) ? 'Blank' : 'Not Blank';
    } else {
        if (!item?.numericValueFrom && !item?.numericValueTo) {
            fieldSummary += 'Not Set';
        } else if (item.numericValueFrom && item.numericValueTo) {
            fieldSummary += `Between ${item.numericValueFrom} and ${item.numericValueTo}`;
        } else if (item.numericValueFrom) {
            fieldSummary += 'Greater than ' + item.numericValueFrom;
        } else {
            fieldSummary += `Less than ${item.numericValueTo}`;
        }
    }
    return <>{fieldSummary}</>;
}